import type { InBoxCompleteness } from './inBoxCompleteness'
import type { PartsAuthenticityMap } from '@redevicer/shared'
import type { DefectMark } from '../components/DefectMarkPad'
import { newPhotoId } from './defectMarks'

const DRAFT_KEY = 'redevicer:pending-sell-draft'
const RESUME_FLAG = 'redevicer:pending-sell-resume'
const ENTRY_KEY = 'redevicer:pending-sell-entry'
const MAX_PHOTO_BYTES = 6 * 1024 * 1024

export type SellListingDraftMode = 'with-imei' | 'smartphone-no-imei' | 'basic-phone' | 'super-fast'

export type GuestSignInReason = 'email' | 'phone'

export interface SellListingDraftPhoto {
  id?: string
  name: string
  type: string
  dataUrl: string
}

export interface SellListingDraftV1 {
  v: 1
  mode: SellListingDraftMode
  savedAt: number
  guestSignInReason: GuestSignInReason | null
  sellerFullName: string
  sellerEmail: string
  sellerPhone: string
  title: string
  price: string
  condition: string
  storage: string
  batteryHealth: string
  batteryCycles: string
  batteryOriginal: boolean
  batteryTestedAt: string
  releaseYear: string
  releaseYearUnknown: boolean
  imei: string
  imeiVerified: boolean
  imeiSkipped: boolean
  phoneModelId: string
  shipmentMethod: 'courier' | 'locker' | 'handover'
  usageDuration: string
  hasFactoryWarranty: boolean
  warrantyRemaining: string
  defectMarks: DefectMark[]
  photoSlots?: Record<string, string>
  inBoxCompleteness: InBoxCompleteness | null
  partsAuth: PartsAuthenticityMap
  listingData: Record<string, unknown> | null
  photos: SellListingDraftPhoto[]
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.readAsDataURL(file)
  })
}

export async function photosToDraft(
  photos: { id: string; file: File }[],
): Promise<SellListingDraftPhoto[]> {
  const out: SellListingDraftPhoto[] = []
  let totalBytes = 0
  for (const photo of photos) {
    if (totalBytes >= MAX_PHOTO_BYTES) break
    if (!photo.file.type.startsWith('image/')) continue
    const dataUrl = await readFileAsDataUrl(photo.file)
    const approxBytes = Math.ceil((dataUrl.length * 3) / 4)
    if (totalBytes + approxBytes > MAX_PHOTO_BYTES) break
    totalBytes += approxBytes
    out.push({
      id: photo.id,
      name: photo.file.name || 'photo.jpg',
      type: photo.file.type || 'image/jpeg',
      dataUrl,
    })
  }
  return out
}

export async function draftToPhotos(
  drafts: SellListingDraftPhoto[],
): Promise<{ id: string; file: File; url: string }[]> {
  const out: { id: string; file: File; url: string }[] = []
  for (const draft of drafts) {
    const res = await fetch(draft.dataUrl)
    const blob = await res.blob()
    const file = new File([blob], draft.name, { type: draft.type || blob.type || 'image/jpeg' })
    out.push({ id: draft.id ?? newPhotoId(), file, url: URL.createObjectURL(file) })
  }
  return out
}

export function saveSellListingDraft(draft: SellListingDraftV1): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    if (draft.guestSignInReason) {
      sessionStorage.setItem(RESUME_FLAG, '1')
      sessionStorage.setItem(
        ENTRY_KEY,
        draft.mode === 'super-fast' ? 'super-fast' : 'wizard',
      )
    }
  } catch {
    /* quota or private mode */
  }
}

export function readPendingSellEntry(): 'wizard' | 'super-fast' | null {
  try {
    const entry = sessionStorage.getItem(ENTRY_KEY)
    if (entry === 'super-fast' || entry === 'wizard') return entry
  } catch {
    /* ignore */
  }
  return null
}

export function loadSellListingDraft(): SellListingDraftV1 | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SellListingDraftV1
    if (parsed?.v !== 1) return null
    return parsed
  } catch {
    return null
  }
}

export function clearSellListingDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY)
    sessionStorage.removeItem(RESUME_FLAG)
    sessionStorage.removeItem(ENTRY_KEY)
  } catch {
    /* ignore */
  }
}

export function takeSellListingDraftResume(): boolean {
  try {
    const pending = sessionStorage.getItem(RESUME_FLAG) === '1'
    if (pending) sessionStorage.removeItem(RESUME_FLAG)
    return pending
  } catch {
    return false
  }
}
